/** Layout de cinco zonas do leitor (telas L-02 e L-08).
 *
 * Abaixo de 768px a sidebar vira drawer modal e o painel direito vira bottom
 * sheet — a mudança é de CSS, mas o `role="dialog"`/`aria-modal` do drawer é de
 * marcação, porque um drawer que cobre a tela sem se anunciar como modal deixa o
 * leitor de tela lendo o conteúdo atrás dele.
 */
import { useEffect, useState } from 'react'

import { AccessibilityPanel } from '@/features/reader/components/AccessibilityPanel'
import { BottomBar } from '@/features/reader/components/BottomBar'
import { RightPanel } from '@/features/reader/components/RightPanel'
import { Sidebar } from '@/features/reader/components/Sidebar'
import { TopBar } from '@/features/reader/components/TopBar'
import { SearchPanelContext } from './searchPanelContext'
import { useReaderStore } from '@/features/reader/stores/readerStore'
import type { BookAccess } from '@/types/api'

const LARGURA_DRAWER = 768

function useEhMobile(): boolean {
  const [ehMobile, setEhMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < LARGURA_DRAWER,
  )

  useEffect(() => {
    const consulta = window.matchMedia(`(max-width: ${LARGURA_DRAWER - 1}px)`)
    const aplicar = () => setEhMobile(consulta.matches)
    aplicar()
    consulta.addEventListener('change', aplicar)
    return () => consulta.removeEventListener('change', aplicar)
  }, [])

  return ehMobile
}

interface ReaderLayoutProps {
  access: BookAccess
  onClose: () => void
  children: React.ReactNode
  /** Conteúdo do painel direito (lista de destaques, tutor). */
  painelDireito?: React.ReactNode
  ferramentas?: React.ReactNode
}

export function ReaderLayout({
  access,
  onClose,
  children,
  painelDireito,
  ferramentas,
}: ReaderLayoutProps) {
  const sidebarAberta = useReaderStore((s) => s.sidebarAberta)
  const alternarSidebar = useReaderStore((s) => s.alternarSidebar)
  const ehMobile = useEhMobile()
  const [searchTarget, setSearchTarget] = useState<HTMLDivElement | null>(null)
  const painel = useReaderStore((s) => s.painelDireito)
  const abrirPainel = useReaderStore((s) => s.abrirPainel)

  return (
    <div className="reader-layout">
      <TopBar access={access} onClose={onClose} />

      <div className="reader-body">
        {sidebarAberta &&
          (ehMobile ? (
            <div className="reader-drawer">
              {/* O backdrop é `aria-hidden`: quem usa teclado fecha com Escape
                  ou com o botão do cabeçalho, não clicando no vazio. */}
              <button
                type="button"
                className="reader-drawer__fundo"
                aria-hidden="true"
                tabIndex={-1}
                onClick={alternarSidebar}
              />
              <div className="reader-drawer__painel" role="dialog" aria-modal="true" aria-label="Sumário">
                <button type="button" onClick={alternarSidebar}>Fechar sumário</button>
                <Sidebar access={access} comoDrawer />
              </div>
            </div>
          ) : (
            <Sidebar access={access} />
          ))}

        <SearchPanelContext.Provider value={searchTarget}>
          <main className="reader-content">{children}</main>
        </SearchPanelContext.Provider>

        <div className="reader-tools-rail">
          <button type="button" className="reader-tools-toggle"
            aria-label={painel === 'ferramentas' ? 'Recolher busca e favoritos' : 'Abrir busca e favoritos'}
            title="Busca e favoritos" aria-expanded={painel === 'ferramentas'}
            aria-controls="reader-study-panel" onClick={() => abrirPainel('ferramentas')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d={painel === 'ferramentas' ? 'm9 5 7 7-7 7' : 'm15 5-7 7 7 7'} />
            </svg>
          </button>
        </div>
        <RightPanel ferramentas={ferramentas} searchTargetRef={setSearchTarget}>{painelDireito}</RightPanel>
      </div>

      <BottomBar access={access} />

      {/* Fora do `reader-body` de propósito: o painel é sobreposto e não deve
          disputar espaço com o conteúdo nem herdar o scroll dele. */}
      <AccessibilityPanel formato={access.format} />
    </div>
  )
}
